<template>
    <v-card elevation="3" rounded="xl" class="itinerary-card">
        <!-- Image with overlaid title bar -->
        <div class="position-relative">
            <v-img v-if="foundItinerary?.full_image_url" :src="foundItinerary.full_image_url" alt="Ship" height="200"
                cover class="rounded-t-xl" />

            <!-- Overlay bar -->
            <div class="title-overlay">
                <span>{{ foundItinerary?.title || 'Itinerary' }}</span>
            </div>
        </div>

        <!-- Top summary -->
        <v-card-text class="pt-4 pb-0">
            <v-sheet class="itinerary-summary px-4 py-3" rounded="lg" elevation="0">
                <div class="text-h6 font-weight-bold mb-1">
                    {{ foundItinerary?.ship_name }}
                </div>

                <!-- Ports -->
                <div class="d-flex align-center text-subtitle-1	 mb-1">
                    <span>{{ startPort }}</span>
                    <v-icon size="16" class="mx-3 text-grey">mdi-arrow-right</v-icon>
                    <span>{{ endPort }}</span>
                </div>

                <!-- Dates -->
                <div class="d-flex align-center text-subtitle-1	 mb-1">
                    <span>{{ startDateStr }}</span>
                    <v-icon size="16" class="mx-3 text-grey">mdi-arrow-right</v-icon>
                    <span>{{ endDateStr }}</span>
                </div>

                <!-- Nights -->
                <div class="text-subtitle-1 font-weight-medium">
                    {{ nights }} Nights
                </div>
            </v-sheet>
        </v-card-text>


        <v-divider class="my-4" />

        <!-- EXPANDABLE: Itinerary -->
        <v-expansion-panels v-model="open" variant="accordion" elevation="0" class="px-2 pb-4">
            <v-expansion-panel rounded="lg" elevation="0">
                <!-- Hide default chevron; provide our own indicator -->
                <v-expansion-panel-title hide-actions class="px-2">
                    <template #default="{ expanded }">
                        <div class="d-flex align-center justify-space-between w-100">
                            <!-- Left: title -->
                            <div class="d-flex align-center">
                                <v-icon class="me-2" style="color:var(--color-secondary)">mdi-map</v-icon>
                                <span class="text-h6 font-weight-bold">Itinerary</span>
                            </div>

                            <!-- Right: custom expand indicator -->
                            <div class="expand-indicator">
                                <v-icon :class="{ rotated: expanded }">mdi-chevron-down</v-icon>
                                <span class="text-subtitle-1 ms-1">{{ expanded ? 'Hide' : 'Show' }}</span>
                            </div>
                        </div>
                    </template>
                </v-expansion-panel-title>

                <v-expansion-panel-text class="pt-4">
                    <!-- Optional route map image (if you have one) -->
                    <v-img v-if="foundItinerary?.map_image_url" :src="foundItinerary.map_image_url" alt="Route Map"
                        height="170" cover class="rounded-lg mb-4" />

                    <!-- Timeline -->
                    <div class="timeline">
                        <div v-for="(stop, i) in fullPortSchedule" :key="i" class="timeline-item">
                            <div class="timeline-dot" :class="stop.seaDay ? 'dot-sea' : 'dot-port'" />
                            <div v-if="i !== fullPortSchedule.length - 1" class="timeline-line" />

                            <div class="timeline-content">
                                <div class="text-subtitle-2 font-weight-bold mb-1">
                                    Day {{ i + 1 }}:
                                    <span v-if="stop.seaDay">
                                        <v-icon size="16" class="me-1"
                                            style="color:var(--color-secondary)">mdi-waves</v-icon>
                                        At Sea
                                    </span>
                                    <span v-else>{{ stop.port }}</span>
                                </div>
                                <div class="text-caption text-grey">{{ stop.date }}</div>
                            </div>
                        </div>

                        <v-alert type="info" variant="text" density="comfortable" class="mt-2">
                            Arrival and departure times are approximate and may vary. If you notice any
                            discrepancies or errors, please report them so our team can update the itinerary.
                        </v-alert>
                    </div>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-card>
</template>

<script>
export default {
    name: 'ItineraryCard',
    props: {
        foundItinerary: { type: Object, required: true },
        startOpen: { type: Boolean, default: false } // set true if you want panel open by default
    },
    data() {
        return { open: this.startOpen ? [0] : [] }
    },
    computed: {
        isOpen() {
            return Array.isArray(this.open) ? this.open.includes(0) : this.open === 0
        },
        numberOfDays() {
            const title = this.foundItinerary?.title ?? ''
            const m = title.match(/(\d+)\s*days?/i)
            return m ? parseInt(m[1], 10) : null
        },
        fullPortSchedule() {
            return this.fillSeaDays(this.foundItinerary?.ports ?? [])
        },
        nights() {
            const t = this.foundItinerary?.title ?? ''
            const m = t.match(/(\d+)\s*days?/i)
            return m ? parseInt(m[1], 10) : null
        },
        startPort() {
            const ports = this.foundItinerary?.ports || []
            return ports.length ? (ports[0]?.port || '—') : '—'
        },
        endPort() {
            const ports = this.foundItinerary?.ports || []
            return ports.length ? (ports[ports.length - 1]?.port || '—') : this.startPort
        },
        startDateObj() {
            return this.parseLocalYmd(this.foundItinerary?.date);
        },
        endDateObj() {
            if (!this.startDateObj || !this.nights) return null;
            const end = new Date(this.startDateObj);
            end.setDate(end.getDate() + this.nights);
            return end;
        },
        startDateStr() {
            return this.startDateObj
                ? this.startDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '';
        },
        endDateStr() {
            return this.endDateObj
                ? this.endDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '';
        }

    },
    methods: {
        parseLocalYmd(ymd) {
            if (!ymd) return null;
            const [y, m, d] = ymd.split('-').map(Number);
            return new Date(y, m - 1, d); // local time, no timezone shift
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        },
        fillSeaDays(portStops) {
            const result = []
            const year = '2025' // adjust if you store year elsewhere

            const parseDate = (dateStr) => {
                // Handles "09 Apr" or "09 Apr 10:00 - 10 Apr 18:00"
                const left = dateStr.split('-')[0].trim()
                const [day, mon] = left.split(' ')
                return new Date(`${day} ${mon} ${year}`)
            }

            for (let i = 0; i < portStops.length; i++) {
                const cur = portStops[i]
                const nxt = portStops[i + 1]
                const curDate = parseDate(cur.date)

                result.push({ ...cur, seaDay: false })

                if (nxt) {
                    const nextDate = parseDate(nxt.date)
                    const diffDays = Math.round((nextDate - curDate) / (1000 * 60 * 60 * 24))
                    for (let j = 1; j < diffDays; j++) {
                        const d = new Date(curDate)
                        d.setDate(curDate.getDate() + j)
                        const formatted = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                        result.push({ date: formatted, port: 'At Sea', seaDay: true })
                    }
                }
            }
            return result
        }
    }
}
</script>

<style scoped>
.position-relative {
    position: relative;
}

.title-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: rgba(35, 83, 107, 0.85);
    /* Your deep blue w/ transparency */
    color: white;
    padding: 8px 16px;
    font-weight: 600;
    font-size: 1.1rem;
}

.text-grey {
    color: rgba(0, 0, 0, .6);
}

.rounded-t-xl {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
}

.quickfacts {
    row-gap: 4px;
}

/* Expand indicator */
.expand-indicator {
    display: inline-flex;
    align-items: center;
    color: var(--color-secondary);
}

.expand-indicator .v-icon {
    transition: transform .18s ease;
}

.expand-indicator .rotated {
    transform: rotate(180deg);
}

/* Timeline */
.timeline {
    position: relative;
    padding-left: 28px;
}

.timeline-item {
    position: relative;
    padding: 10px 0 14px;
}

.timeline-dot {
    position: absolute;
    left: 12px;
    top: 16px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.dot-port {
    background: var(--color-secondary, #26a69a);
}

.dot-sea {
    background: rgba(0, 0, 0, .25);
}

.timeline-line {
    position: absolute;
    left: 16px;
    top: 26px;
    width: 2px;
    height: calc(100% - 24px);
    background: var(--color-secondary, #26a69a);
    opacity: .35;
}

.timeline-content {
    margin-left: 28px;
}

/* Mobile tweaks */
@media (max-width: 600px) {
    .timeline {
        padding-left: 24px;
    }

    .timeline-dot {
        left: 10px;
    }

    .timeline-line {
        left: 14px;
    }
}
</style>
